import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import AdminList from '../../../../../ui/admin/AdminList';
import { ChannelGroup } from '../../../../../../shared/types';
import {
    DELETE_CHANNEL_GROUP,
    REORDER_CHANNEL_GROUPS,
} from '../../../../../../gql/channelGroups';
import {
    MessageTypes,
    useNotification,
} from '../../../../../providers/NotificationProvider';
import useChannelGroupRowDragging from '../../hooks/useChannelGroupRowDragging';
import getChannelGroupColumnDefs from './columnDefs';
import Footer from '../Footer';
import DeleteDialog from '../DeleteDialog';
import { GET_CHANNELS_AND_GROUPS_FOR_PAGE } from '../../../../../../gql/channels';

const title = 'Channel Groups';
const name = 'channel-groups';

interface ChannelGroupOrderingTableProps {
    channelGroups: ChannelGroup[];
    onRefetch: () => void;
}

const ChannelGroupOrderingTable: React.FC<ChannelGroupOrderingTableProps> = ({
    channelGroups,
    onRefetch,
}) => {
    const { showMessage } = useNotification();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<ChannelGroup | null>(
        null
    );

    const {
        channelGroups: orderedGroups,
        weightsDirty,
        onRowDragEnd,
        resetRowDragging,
    } = useChannelGroupRowDragging(channelGroups);

    const [reorderChannelGroups, { loading: savingWeights }] = useMutation(
        REORDER_CHANNEL_GROUPS
    );
    const [deleteGroup, { loading: deleting }] = useMutation(
        DELETE_CHANNEL_GROUP,
        {
            refetchQueries: [{ query: GET_CHANNELS_AND_GROUPS_FOR_PAGE }],
        }
    );

    const onDeleteClick = useCallback((group: ChannelGroup) => {
        setSelectedGroup(group);
        setDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setDialogOpen(false);
    }, []);

    const onConfirmDelete = useCallback(async () => {
        if (!selectedGroup?._id) return;
        const groupName = selectedGroup.name;
        try {
            await deleteGroup({ variables: { id: selectedGroup._id } });
            showMessage(
                `The channel group ${groupName} has been removed.`,
                MessageTypes.info
            );
            setSelectedGroup(null);
            setDialogOpen(false);
            resetRowDragging();
        } catch {
            showMessage(
                'Failed to delete group. Please try again.',
                MessageTypes.error
            );
        }
    }, [deleteGroup, selectedGroup, showMessage, resetRowDragging]);

    const saveWeights = useCallback(async () => {
        try {
            await reorderChannelGroups({
                variables: {
                    groupIds: orderedGroups.map(
                        (group: ChannelGroup) => group._id
                    ),
                },
            });
            showMessage(
                'Channel group ordering has been saved.',
                MessageTypes.info
            );
            await onRefetch();
            resetRowDragging();
        } catch {
            showMessage(
                'Failed to save channel group ordering. Please try again.',
                MessageTypes.error
            );
        }
    }, [
        orderedGroups,
        reorderChannelGroups,
        onRefetch,
        resetRowDragging,
        showMessage,
    ]);

    const footer = useMemo(
        () => (
            <Footer
                wrapperSx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 2,
                }}
                disabled={!weightsDirty || savingWeights}
                onSave={saveWeights}
                buttonId="save-channel-group-weights-button"
            />
        ),
        [weightsDirty, savingWeights, saveWeights]
    );

    const columnDefs = useMemo(
        () =>
            getChannelGroupColumnDefs({
                apiName: name,
                onDeleteClick,
            }),
        [onDeleteClick]
    );

    return (
        <>
            <AdminList
                title={title}
                name={name}
                items={orderedGroups}
                loading={false}
                columns={columnDefs}
                defaultColDef={{
                    sortable: false,
                }}
                gridOptions={{
                    pagination: false,
                    rowDragManaged: true,
                    animateRows: true,
                    onRowDragEnd,
                }}
                showAdd
                addButtonLabel="New group"
                entityForCaptionText="groups"
                pagination={false}
                renderGridWhenEmpty
                footer={footer}
            />
            <DeleteDialog
                dialogOpen={dialogOpen}
                selectedGroup={selectedGroup}
                deleting={deleting}
                onClose={closeDialog}
                onConfirmDelete={onConfirmDelete}
            />
        </>
    );
};

export default ChannelGroupOrderingTable;
